import { ISSUE, ISSUE_TARGET, configIssue } from '../catalog/issue';
import type { ConfigIssue } from '../catalog/issue';
import type { LoadId, RoomId } from '../ids';
import { newRoomId } from '../ids';
import type { CommandResult, RcuConfig, Room } from '../model';
import { findRoom } from '../query/lookup';
import { fail } from '../result';
import { changedBy } from './outcome';

export interface RoomDraft {
  readonly name: string;
  readonly note: string;
}

export interface RoomPatch {
  readonly name?: string;
  readonly note?: string;
}

const roomNotFound = (id: RoomId): ConfigIssue =>
  configIssue(ISSUE.ROOM_NOT_FOUND, { kind: ISSUE_TARGET.ROOM, id }, id);

const nameMissing = (): ConfigIssue =>
  configIssue(ISSUE.NAME_REQUIRED, { kind: ISSUE_TARGET.ROOM }, 'Room');

export const addRoom = (
  config: RcuConfig,
  draft: RoomDraft,
  now: string,
): CommandResult<RoomId> => {
  if (draft.name.trim() === '') return fail(nameMissing());

  const room: Room = {
    id: newRoomId(),
    name: draft.name.trim(),
    note: draft.note,
    createdAt: now,
  };
  return changedBy({ ...config, rooms: [...config.rooms, room] }, room.id, now);
};

export const updateRoom = (
  config: RcuConfig,
  id: RoomId,
  patch: RoomPatch,
  now: string,
): CommandResult<RoomId> => {
  const room = findRoom(config, id);
  if (room === undefined) return fail(roomNotFound(id));

  const name = patch.name === undefined ? room.name : patch.name.trim();
  if (name === '') return fail(nameMissing());

  const next: Room = { ...room, ...patch, name };
  return changedBy(
    { ...config, rooms: config.rooms.map((current) => (current.id === id ? next : current)) },
    id,
    now,
  );
};

/**
 * Removes a room. Its loads are unassigned rather than deleted, so they stay
 * controllable under the Unassigned tab.
 */
export const removeRoom = (config: RcuConfig, id: RoomId, now: string): CommandResult<number> => {
  if (findRoom(config, id) === undefined) return fail(roomNotFound(id));

  const loads = config.loads.map((load) =>
    load.roomId === id ? { ...load, roomId: null } : load,
  );
  const unassigned = config.loads.filter((load) => load.roomId === id).length;

  return changedBy(
    {
      ...config,
      rooms: config.rooms.filter((room) => room.id !== id),
      loads,
      defaultRoomId: config.defaultRoomId === id ? null : config.defaultRoomId,
    },
    unassigned,
    now,
  );
};

/** Moves a batch of loads into a room, or out of every room when `roomId` is null. */
export const assignLoadsToRoom = (
  config: RcuConfig,
  loadIds: readonly LoadId[],
  roomId: RoomId | null,
  now: string,
): CommandResult<number> => {
  if (roomId !== null && findRoom(config, roomId) === undefined) return fail(roomNotFound(roomId));

  const missing = loadIds.find((id) => !config.loads.some((load) => load.id === id));
  if (missing !== undefined) {
    return fail(configIssue(ISSUE.LOAD_NOT_FOUND, { kind: ISSUE_TARGET.LOAD, id: missing }, missing));
  }

  const moving = new Set<string>(loadIds);
  return changedBy(
    {
      ...config,
      loads: config.loads.map((load) => (moving.has(load.id) ? { ...load, roomId } : load)),
    },
    moving.size,
    now,
  );
};

/** Room this tablet belongs to, preselected on the controls page. */
export const setDefaultRoom = (
  config: RcuConfig,
  roomId: RoomId | null,
  now: string,
): CommandResult<RoomId | null> => {
  if (roomId !== null && findRoom(config, roomId) === undefined) return fail(roomNotFound(roomId));
  return changedBy({ ...config, defaultRoomId: roomId }, roomId, now);
};
